import { Button } from "@/components/shared/buttons/Button";
import { FileDropbox } from "@/components/shared/media/FileDropbox";
import { UploadFile } from "@/components/shared/media/UploadFile";
import { FileError } from "@/config/constants";
import {
    Alert,
    AlertTitle,
    Stack,
    SxProps,
    Theme,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useIsMutating } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";

const buttonSx: SxProps<Theme> = {
    borderRadius: 2,
    py: 1,
};

interface IProps {
    /**
     * Callback to close the dialog
     */
    onClose: () => void;
}

/**
 * PreprocessForm component
 */
export const PreprocessForm = ({ onClose }: IProps) => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
    const activeUploads = useIsMutating({ mutationKey: ["fileUpload"] });

    const {
        setError,
        watch,
        formState: { errors },
    } = useFormContext();

    // Make sure the form has a file attached
    const validateFile = () => {
        if (!watch("file")) {
            setError("file", {
                type: FileError.Required,
                message: "A file is required to import clauses.",
            });
        }
    };

    return (
        <>
            <Stack width={"100%"} gap={1}>
                <FileDropbox
                    name="file"
                    accept={{
                        "text/csv": [".csv"],
                        "application/vnd.ms-excel": [".xlsx"],
                    }}
                    description="Upload a CSV or Excel file to import clauses."
                />
                <UploadFile name="file" />
                {errors.file && (
                    <Alert severity="error" sx={{ width: "100%" }}>
                        <AlertTitle>{errors.file.type as string}</AlertTitle>
                        {errors.file.message as string}
                    </Alert>
                )}
            </Stack>
            <Stack
                direction={"row"}
                gap={3}
                maxWidth={isSmall ? 118 : 379}
                width={"100%"}
            >
                <Button
                    type="button"
                    variant={"outlined"}
                    color={"dark"}
                    sx={buttonSx}
                    fullWidth
                    onClick={onClose}
                    disabled={activeUploads > 0}
                >
                    Cancel
                </Button>

                <Button
                    variant={"contained"}
                    color={"dark"}
                    disableElevation
                    sx={buttonSx}
                    fullWidth
                    onClick={validateFile}
                    disabled={activeUploads > 0}
                >
                    Continue
                </Button>
            </Stack>
        </>
    );
};
